import { useCallback } from "react";
import {
  formatNameFromPersonObj,
  hasAllRequiredRaceFields,
  isRaceDataPresent,
} from "../utils/utils";
import { getPoldTrigger } from "../utils/getPoldTrigger";
import { validateSizeAndWeight } from "../utils/validateSizeAndWeight";
import { validateAgencySpecificReview } from "./AgencySpecificValidations";

/**
 * Each validator returns:
 *  { ok: boolean, errors: string[], sections: object, warnings?: string[] }
 * sections is a shape describing which UI to show in the unified modal.
 * e.g. { drugTypes: true, race: { ticketId: string }, axon: true, pold: true }
 */

function validateYouthfulOffender(yoeJuvenileStatuses = []) {
  const yoePossible = (yoeJuvenileStatuses || []).filter(
    pos => pos?.isYoeEligible === true && pos?.yoe === false,
  );
  const warnings = yoePossible.map(
    pos =>
      `${formatNameFromPersonObj(
        pos?.person,
      )} is eligible for Youthful Offender status but not marked as such.`,
  );
  return { ok: true, errors: [], sections: {}, warnings };
}

function validateDrugTypes({ incident }) {
  if (!incident?.ori?.requireDrugType)
    return { ok: true, errors: [], sections: {} };
  const hasDrugTypes =
    Array.isArray(incident?.drugTypes) && incident.drugTypes.length > 0;
  if (hasDrugTypes) return { ok: true, errors: [], sections: {} };

  return {
    ok: false,
    errors: ["Please select the Drugs Involved."],
    sections: { drugTypes: true },
  };
}

function getOperatorAndTicketData({ incident, incidentPeople }) {
  const firstOperatorTicket = incident?.incidentTickets?.find(
    ticket => !!ticket?.operator?.id,
  );
  const ticketWithPerceivedRace = incident?.incidentTickets?.find(
    ticket => !!ticket?.perceivedRace?.id,
  );
  const fallbackTicket = incident?.incidentTickets?.[0] || null;
  const raceTicket =
    firstOperatorTicket || ticketWithPerceivedRace || fallbackTicket;
  const operatorId =
    firstOperatorTicket?.operator?.id || raceTicket?.operator?.id;

  // Resolve operator person object
  const operator = operatorId
    ? incidentPeople?.find(p => p?.id === operatorId)
    : incident?.personIncidentSpecifics?.find(
          pis => pis?.incidentInvolvement?.name === "Operator",
        )
      ? incident?.personIncidentSpecifics?.find(
          pis => pis?.incidentInvolvement?.name === "Operator",
        )?.person
      : incident?.vehicleIncidentSpecifics?.find(vis => vis?.operator)
          ?.operator;

  return { firstOperatorTicket, ticketWithPerceivedRace, raceTicket, operator };
}

function validateRaceData({ incident, incidentPeople, raceTicket }) {
  // Only required if signal is present
  const raceDataCodePresent = incident?.stateWideEvents?.some(
    e => e?.name === "Race Data Present",
  );

  const {
    firstOperatorTicket,
    ticketWithPerceivedRace,
    raceTicket: autoTicket,
    operator,
  } = getOperatorAndTicketData({ incident, incidentPeople });
  const ticket = raceTicket || autoTicket;
  const ticketHasRaceData = isRaceDataPresent(ticket);

  if (!(raceDataCodePresent || ticketHasRaceData)) {
    return { ok: true, errors: [], sections: {} };
  }

  const ticketHasAllRaceData = hasAllRequiredRaceFields(ticket);

  const ticketHasAllUoF =
    !!ticket?.uofInstance?.id &&
    (ticket?.uofInstanceTypes?.length || 0) > 0 &&
    (ticket?.uofInjuryDetails?.length || 0) > 0;

  const operatorHasDob = !!operator?.personalAttribute?.dob;
  const operatorHasGender = !!operator?.personalAttribute?.gender?.id;

  const allGood =
    ticketHasAllRaceData &&
    ticketHasAllUoF &&
    operatorHasDob &&
    operatorHasGender;

  if (allGood) return { ok: true, errors: [], sections: {} };

  const ticketIdToFocus =
    firstOperatorTicket?.id ??
    firstOperatorTicket?.uuid ??
    ticketWithPerceivedRace?.id ??
    ticketWithPerceivedRace?.uuid ??
    ticket?.id ??
    ticket?.uuid ??
    null;

  return {
    ok: false,
    errors: [
      "Race data (and/or required Use of Force details) are incomplete.",
    ],
    sections: { race: { ticketId: ticketIdToFocus } },
  };
}

function validateAxon({ axonEnabledForIncident, incident }) {
  if (!axonEnabledForIncident) return { ok: true, errors: [], sections: {} };
  const hasIncidentFlag = incident?.incidentFlags?.some(flag => !!flag?.id);
  return {
    ok: hasIncidentFlag,
    errors: hasIncidentFlag
      ? []
      : ["Axon validation requires an incident flag."],
    sections: { axon: !hasIncidentFlag },
  };
}

function validatePold({ poldOptOut, incident }) {
  const { showPold, isPoldComplete, hasBarNight } = getPoldTrigger({
    incident,
    poldOptOut,
  });

  if (!showPold) return { ok: true, errors: [], sections: {} };
  if (isPoldComplete) return { ok: true, errors: [], sections: {} };

  const triggerSummary = hasBarNight
    ? "alcohol and a Bar/Night Club IBR Scene/Loc Type"
    : "alcohol";

  const hasPold = incident?.poldInstance?.hasPold;
  if (typeof hasPold !== "boolean") {
    return {
      ok: false,
      errors: [
        `This incident involves ${triggerSummary}. Please answer the POLD question.`,
      ],
      sections: { pold: true },
    };
  }

  return {
    ok: false,
    errors: ["Please provide the POLD business or address."],
    sections: { pold: true },
  };
}

async function validateSizeAndWeightReview({
  incident,
  userId,
  client,
  sizeAndWeightEnabledForIncident,
}) {
  if (!sizeAndWeightEnabledForIncident) {
    return { ok: true, errors: [], sections: {} };
  }
  const res = await validateSizeAndWeight(incident, userId, client);
  if (res?.isValid) return { ok: true, errors: [], sections: {} };
  return {
    ok: false,
    errors: res?.errors || [
      "Size and Weight validation failed. Please review the frame.",
    ],
    sections: { sizeAndWeight: true },
  };
}

/**
 * Hook: returns an async validateForReady() fn
 * validateForReady({ include?: string[], exclude?: string[] }) ->
 *  { ok: boolean, errors: string[], sections, warnings: string[] }
 */
export function useReviewValidation({
  incident,
  incidentPeople,
  yoeJuvenileStatuses,
  axonEnabledForIncident = false,
  poldOptOut = false,
  sizeAndWeightEnabledForIncident = false,
  userId,
  client,
}) {
  const validateForReady = useCallback(
    async (options = {}) => {
      const aggregate = (acc, res) => {
        if (!res.ok) acc.ok = false;
        acc.errors.push(...(res.errors || []));
        acc.warnings.push(...(res.warnings || []));
        acc.sections = {
          ...acc.sections,
          ...(res.sections || {}),
        };
        return acc;
      };

      const allValidations = [
        {
          key: "youthfulOffender",
          run: () => validateYouthfulOffender(yoeJuvenileStatuses),
        },
        { key: "drugTypes", run: () => validateDrugTypes({ incident }) },
        {
          key: "raceData",
          run: () =>
            validateRaceData({
              incident,
              incidentPeople,
              raceTicket: options?.raceTicket,
            }),
        },
        {
          key: "axon",
          run: () => validateAxon({ axonEnabledForIncident, incident }),
        },
        { key: "pold", run: () => validatePold({ poldOptOut, incident }) },
        {
          key: "sizeAndWeight",
          run: () =>
            validateSizeAndWeightReview({
              incident,
              userId,
              client,
              sizeAndWeightEnabledForIncident,
            }),
        },
        {
          key: "agencySpecific",
          run: () => validateAgencySpecificReview({ incident }),
        },
      ];

      const include = Array.isArray(options.include)
        ? options.include
        : options.include
          ? [options.include]
          : null;
      const exclude = Array.isArray(options.exclude)
        ? options.exclude
        : options.exclude
          ? [options.exclude]
          : [];

      const selected = allValidations.filter(v => {
        if (include) return include.includes(v.key);
        return !exclude.includes(v.key);
      });

      let results = { ok: true, errors: [], sections: {}, warnings: [] };
      for (const validation of selected) {
        const res = await validation.run();
        results = aggregate(results, res);
      }

      return results;
    },
    [
      incident,
      incidentPeople,
      yoeJuvenileStatuses,
      axonEnabledForIncident,
      poldOptOut,
      sizeAndWeightEnabledForIncident,
      userId,
      client,
    ],
  );

  return { validateForReady };
}
